import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import {
  TextInput,
  Button,
  Stack,
  Text,
  LoadingOverlay,
  Box,
  Group,
  Title,
} from "@mantine/core";
import PasswordRequirementInput from "./common/PasswordRequirementInput";
import { isPasswordValid } from "../utils/passwordValidation";
import { API_CONFIG, MICROSERVICE_URLS } from "../config/api";
import styles from "./ForgotPassword.module.css";

// Server-side gatekeeper. Cognito with PreventUserExistenceErrors=ENABLED
// hides UNCONFIRMED / FORCE_CHANGE_PASSWORD from the client, so we ask the
// backend what action to take before calling Amplify resetPassword.
async function fetchRecoveryAction(email) {
  const baseUrl =
    MICROSERVICE_URLS[API_CONFIG.ENDPOINTS.ACCOUNT_RECOVERY.service];
  if (!baseUrl) {
    return { action: "reset_password" };
  }
  const url = `${baseUrl.replace(/\/$/, "")}${API_CONFIG.ENDPOINTS.ACCOUNT_RECOVERY.path}`;
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) return { action: "reset_password" };
    const data = await response.json();
    return data && data.action ? data : { action: "reset_password" };
  } catch {
    return { action: "reset_password" };
  }
}

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (countdown > 0) return;

    setIsLoading(true);
    setError({});
    setSuccessMessage("");

    try {
      const { action } = await fetchRecoveryAction(email);

      if (action === "invitation_sent") {
        setSuccessMessage(
          "Your account was set up by invitation but never activated. We've re-sent the invitation email — use the link inside to set your password.",
        );
        return;
      }

      if (action === "verify_email") {
        navigate("/verify-email", {
          state: {
            username: email,
            infoMessage:
              "Your email isn't verified yet. We've sent a new verification code — confirm your email, then reset your password.",
          },
        });
        return;
      }

      // action === "reset_password" (default / fallback)
      await resetPassword({ username: email });
      setCodeSent(true);
      setCountdown(30);
      setSuccessMessage("A verification code has been sent to your email");
    } catch (error) {
      setError({ top: error.message || "Failed to send verification code" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!isPasswordValid(newPassword)) {
      setError({ password: "Password is missing required characters" });
      return;
    }

    setIsLoading(true);
    setError({});
    setSuccessMessage("");
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      setSuccessMessage("Password successfully reset");
      setTimeout(() => {
        setIsLoading(false);
        navigate("/login");
      }, 2000);
    } catch (error) {
      setError({ top: error.message || "Failed to reset password" });
      setIsLoading(false);
    }
  };

  return (
    <Box className={styles.pageWrapper}>
      {isLoading && (
        <LoadingOverlay visible={true} overlayProps={{ blur: 2 }} />
      )}

      {/* Background blobs */}
      <Box className={styles.backgroundBlobs}>
        <Box className={styles.rightBlob} />
      </Box>

      {/* Header with Logo */}
      <header className={styles.header}>
        <img
          src="/images/pagoda-logo-horizontal.png"
          alt="Pagoda"
          className={styles.logo}
        />
      </header>

      {/* Main Content */}
      <main className={styles.mainContent}>
        {/* Reset Container - wraps title section and form */}
        <Stack gap="lg" className={styles.resetContainer}>
          {/* Title Section */}
          <Stack gap="xs" className={styles.titleSection}>
            <Title order={1} className={styles.title}>
              {codeSent ? "Check your email" : "Reset your password"}
            </Title>
            <Text size="sm" c="dimmed" className={styles.subtitle}>
              {codeSent
                ? "Enter the verification code we sent you"
                : "Enter your email and we'll send you a reset code"}
            </Text>
          </Stack>

          {/* Form Card */}
          <Box className={styles.formCard}>
            {error.top && (
              <Text className={styles.errorMessage}>{error.top}</Text>
            )}

            {successMessage && (
              <Text className={styles.successMessage}>{successMessage}</Text>
            )}

            <form onSubmit={codeSent ? handleResetPassword : handleSendCode}>
              <Stack className={styles.formStack}>
                <TextInput
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  radius="sm"
                  size="md"
                  required
                  disabled={codeSent}
                  styles={{
                    label: {
                      fontWeight: 500,
                      marginBottom: 8,
                      color: "#101820",
                    },
                  }}
                />

                {codeSent && (
                  <>
                    <TextInput
                      label="Verification Code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Enter verification code"
                      radius="sm"
                      size="md"
                      required
                      styles={{
                        label: {
                          fontWeight: 500,
                          marginBottom: 8,
                          color: "#101820",
                        },
                      }}
                    />

                    <PasswordRequirementInput
                      label="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      error={error.password}
                      placeholder="Enter new password"
                      radius="sm"
                      size="md"
                      required
                      showRequirements={true}
                    />
                  </>
                )}

                <Button
                  type="submit"
                  loading={isLoading}
                  fullWidth
                  radius="sm"
                  size="md"
                  className={styles.submitButton}
                >
                  {codeSent ? "Reset Password" : "Send Reset Code"}
                </Button>

                {codeSent && (
                  <button
                    type="button"
                    onClick={handleSendCode}
                    className={`${styles.resendLink} ${countdown > 0 ? styles.resendLinkDisabled : ""}`}
                    disabled={countdown > 0}
                  >
                    {countdown > 0
                      ? `Send new code (${countdown}s)`
                      : "Send new code"}
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className={styles.backLink}
                >
                  Back to Login
                </button>
              </Stack>
            </form>
          </Box>
        </Stack>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <Text size="sm" className={styles.copyright}>
          © Copyright {new Date().getFullYear()}, Pagoda Labs, Inc.
        </Text>
        <Group gap="md" className={styles.footerLinks}>
          <a
            href="https://pagoda.co/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Privacy Policy
          </a>
          <a
            href="https://pagoda.co/terms"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Terms & Conditions
          </a>
        </Group>
      </footer>
    </Box>
  );
}

export default ForgotPassword;
